import SEO from '../components/SEO';

export default function PrivacyPolicyPage() {
  return (
    <div className="flex-1 bg-white py-12">
      <SEO
        title="Privacy Policy | Waybook"
        description="Read Waybook's Privacy Policy to understand how we collect, use, and protect your personal data. GDPR, UK GDPR, and CCPA compliant."
        keywords="privacy policy, data protection, GDPR, user privacy, personal data"
        canonicalUrl="https://www.waybook.me/privacy-policy"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Privacy Policy',
          description: 'Privacy Policy for Waybook',
          url: 'https://www.waybook.me/privacy-policy'
        }}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold mb-8">Privacy Policy</h1>

        <div className="prose max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-semibold mb-4">1. Overview</h2>
            <p className="text-gray-700">
              Waybook is a worldwide travel companion available on iOS, Android, and web. We
              respect your privacy and are committed to protecting personal data in line with
              applicable laws, including the GDPR (EU/EEA), UK GDPR, and CCPA/CPRA (California),
              where applicable.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">2. Scope & Controller</h2>
            <p className="text-gray-700">
              This Policy applies to your use of the Waybook mobile apps and website. For
              privacy inquiries or requests, contact us at <a href="mailto:privacy@waybook.me">privacy@waybook.me</a>.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">3. Data We Collect</h2>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Account data: name, email, profile details.</li>
              <li>Usage data: app interactions, preferences, device and app version.</li>
              <li>Technical data: IP address, device identifiers, OS, browser, crash logs.</li>
              <li>Content data: trips, notes, media and files you choose to upload.</li>
              <li>Communications: messages you send to support or via feedback forms.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">4. How We Use Data</h2>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Provide and improve core functionality and features.</li>
              <li>Personalize content, recommendations and notifications.</li>
              <li>Maintain security, prevent fraud and abuse, and debug issues.</li>
              <li>Communicate about updates, service changes and marketing (opt‑in).</li>
              <li>Comply with legal obligations and enforce our Terms.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">5. Legal Bases (GDPR)</h2>
            <p className="text-gray-700">
              We process personal data based on: (a) performance of a contract (providing the
              service), (b) legitimate interests (safety, analytics, improvement), (c) consent
              (where required, e.g., marketing), and (d) legal obligations.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">6. Sharing & Processors</h2>
            <p className="text-gray-700 mb-3">
              We use trusted service providers (e.g., cloud hosting, analytics, email) to
              operate Waybook. These providers only process data on our instructions and are
              bound by confidentiality and security obligations. We do not sell personal data.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">7. International Transfers</h2>
            <p className="text-gray-700">
              If data is transferred internationally, we rely on appropriate safeguards such as
              Standard Contractual Clauses and equivalent mechanisms where required by law.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">8. Retention</h2>
            <p className="text-gray-700">
              We retain personal data only as long as necessary for the purposes described, and
              as required by law. You can request deletion of your account and associated data.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">9. Your Rights</h2>
            <p className="text-gray-700 mb-3">
              Depending on your location, you may have rights to access, correct, delete,
              restrict or object to processing, data portability, and to withdraw consent.
              California residents may opt-out of certain sharing and request disclosures.
            </p>
            <p className="text-gray-700">
              To exercise rights, contact <a href="mailto:privacy@waybook.me">privacy@waybook.me</a>. We will verify and respond
              in accordance with applicable law.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">10. Children</h2>
            <p className="text-gray-700">
              Waybook is not intended for children under 13. We do not knowingly collect data
              from children. If you believe a child provided data, contact us and we will
              take appropriate steps to delete it.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">11. Security</h2>
            <p className="text-gray-700">
              We implement technical and organizational measures to protect personal data
              against unauthorized access, loss, misuse, or alteration.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">12. Changes</h2>
            <p className="text-gray-700">
              We may update this Policy from time to time. Material changes will be notified
              via the app or website. The “Last updated” date will be adjusted accordingly.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-2">Contact</h2>
            <p className="text-gray-700">
              Email: <a href="mailto:privacy@waybook.me">privacy@waybook.me</a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
